import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, map, catchError, of } from 'rxjs';
import { InvoiceService } from './invoice.service';
import { ChargeService } from './charge.service';
import type { InvoiceDTO, ChargeDTO } from '../core/models';
import type { ListInvoiceFilterSpec } from './service-common';

/** Formato yyyy-MM-dd'T'HH:mm:ss.SSS'Z' para filtros de fecha en /charges/list (dueDate, etc.) */
function toChargeDateISO(d: Date): string {
  return d.toISOString();
}

export interface CollectionSummary {
  amount: number;
  count: number;
}

export interface ProjectionSummary {
  amount: number;
  count: number;
}

export interface ChargeWithInvoice extends ChargeDTO {
  invoice?: InvoiceDTO;
}

export interface DashboardData {
  collection: CollectionSummary;
  projection: ProjectionSummary;
  upcomingCharges: ChargeWithInvoice[];
  overdueCharges: ChargeWithInvoice[];
  /** Cargos pagados en el período (últimos primero), GET /charges/list */
  recentPayments: ChargeWithInvoice[];
  /** Facturación (total de facturas) agrupada por día de emisión en el período */
  salesByDay: { date: string; total: number; count: number }[];
  heatMapData: { date: string; amount: number; count: number }[];
  rates: {
    collectionRate: number;
    overdueRate: number;
    totalIssued: number;
    totalPaid: number;
    totalPending: number;
  };
  invoicesByStatus: Record<string, number>;
  loading: boolean;
  error?: string;
}

/** Período del reporte: hoy, semana (lun–hoy), mes, año o rango personalizado */
export type DashboardPeriodType = 'today' | 'week' | 'month' | 'year' | 'custom';

export interface DashboardPeriod {
  type: DashboardPeriodType;
  dateFrom?: string; // yyyy-MM-dd para custom
  dateTo?: string;   // yyyy-MM-dd para custom
}

export function getPeriodRange(period: DashboardPeriod): { from: Date; to: Date } {
  const now = new Date();
  const startOfDay = (d: Date) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };
  const endOfDay = (d: Date) => {
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x;
  };

  switch (period.type) {
    case 'today':
      return { from: startOfDay(now), to: endOfDay(now) };
    case 'week': {
      const d = new Date(now);
      const day = d.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      const monday = new Date(d);
      monday.setDate(d.getDate() + diff);
      return { from: startOfDay(monday), to: endOfDay(now) };
    }
    case 'month':
      return {
        from: startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)),
        to: endOfDay(new Date(now.getFullYear(), now.getMonth() + 1, 0))
      };
    case 'year':
      return {
        from: startOfDay(new Date(now.getFullYear(), 0, 1)),
        to: endOfDay(new Date(now.getFullYear(), 11, 31))
      };
    case 'custom':
      const from = period.dateFrom ? startOfDay(new Date(period.dateFrom + 'T00:00:00')) : startOfDay(new Date(now.getFullYear(), 0, 1));
      const to = period.dateTo ? endOfDay(new Date(period.dateTo + 'T00:00:00')) : endOfDay(now);
      return { from, to };
    default:
      return {
        from: startOfDay(new Date(now.getFullYear(), 0, 1)),
        to: endOfDay(now)
      };
  }
}

const STATUS_PAID = 'PAID';
const STATUS_PARTIALLY_PAID = 'PARTIALLY_PAID';
const STATUS_ISSUED = 'ISSUED';
const CHARGE_PENDING = 'PENDING';
const CHARGE_DUE = 'DUE';
const CHARGE_SCHEDULED = 'SCHEDULED';
const CHARGE_PAID = 'PAID';
const CHARGE_CANCELLED = 'CANCELLED';
const PENDING_STATUSES = [CHARGE_PENDING, CHARGE_DUE, CHARGE_SCHEDULED];

@Injectable({ providedIn: 'root' })
export class DashboardService {
  private invoiceService = inject(InvoiceService);
  private chargeService = inject(ChargeService);

  /**
   * Carga datos para el dashboard en el período indicado.
   * Usa el servicio de cargos (GET /charges/list) para proyección, próximos y vencidos; facturas solo para cobranza, heat map y estados.
   */
  loadDashboard(period: DashboardPeriod = { type: 'year' }): Observable<DashboardData> {
    const now = new Date();
    const { from, to } = getPeriodRange(period);
    const fromStr = toChargeDateISO(from);
    const toStr = toChargeDateISO(to);
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const todayStr = toChargeDateISO(startOfToday);
    const in30End = new Date(startOfToday);
    in30End.setDate(in30End.getDate() + 30);
    in30End.setHours(23, 59, 59, 999);
    const in30Str = toChargeDateISO(in30End);

    const invoiceSpec: ListInvoiceFilterSpec = {
      'status[in]': ['PAID', 'PARTIALLY_PAID', 'ISSUED'],
      'createdAt[gte]': from.toISOString(),
      'createdAt[lte]': to.toISOString()
    };

    const invoices$ = this.invoiceService.listInvoices(invoiceSpec, { page: 0, size: 200 }).pipe(
      map((res) => (res.ok && res.body ? res.body : []))
    );

    const projectionCharges$ = this.chargeService.listCharges(
      {
        'dueDate[gte]': fromStr,
        'dueDate[lte]': toStr,
        'status[in]': PENDING_STATUSES
      },
      { page: 0, size: 500 }
    ).pipe(
      map((res) => (res.ok && res.body ? res.body : []))
    );

    const upcomingCharges$ = this.chargeService.listCharges(
      {
        'dueDate[gte]': todayStr,
        'dueDate[lte]': in30Str,
        'status[in]': PENDING_STATUSES
      },
      { page: 0, size: 50 }
    ).pipe(
      map((res) => (res.ok && res.body ? res.body : []))
    );

    const overdueCharges$ = this.chargeService.listCharges(
      { 'dueDate[lt]': todayStr },
      { page: 0, size: 500 }
    ).pipe(
      map((res) => {
        if (!res.ok || !res.body) return [];
        return res.body.filter(
          (c) => c.status !== CHARGE_PAID && c.status !== CHARGE_CANCELLED
        );
      })
    );

    const recentPaid$ = this.chargeService
      .listCharges(
        {
          'status[in]': [CHARGE_PAID],
          'paidAt[gte]': fromStr,
          'paidAt[lte]': toStr
        },
        { page: 0, size: 30, sort: ['paidAt,desc'] }
      )
      .pipe(
        map((res) => (res.ok && res.body ? res.body : [])),
        catchError(() => of([] as ChargeDTO[])),
        map((list) =>
          [...list]
            .sort((a, b) => {
              const ta = this.parseDate(a.paidAt)?.getTime() ?? 0;
              const tb = this.parseDate(b.paidAt)?.getTime() ?? 0;
              return tb - ta;
            })
            .slice(0, 15)
            .map((c) => ({ ...c }) as ChargeWithInvoice)
        )
      );

    return forkJoin({
      invoices: invoices$,
      projectionCharges: projectionCharges$,
      upcomingCharges: upcomingCharges$,
      overdueCharges: overdueCharges$,
      recentPayments: recentPaid$
    }).pipe(
      map(({ invoices, projectionCharges, upcomingCharges, overdueCharges, recentPayments }) => {
        const collection = this.computeCollection(invoices, from, to);
        const heatMapData = this.computeHeatMap(invoices, from, to);
        const salesByDay = this.computeSalesByDay(invoices, from, to);
        const invoicesByStatus = this.computeInvoicesByStatus(invoices);
        const totalIssued = invoices.reduce<number>((s, i) => s + (Number(i.total) || 0), 0);
        const totalPaid = invoices.reduce<number>((s, i) => s + this.amountPaidFromInvoice(i), 0);
        const totalPending = invoices.reduce<number>((s, i) => s + (Number(i.amountDue) || 0), 0);

        const projectionAmount = projectionCharges.reduce((s, c) => s + (Number(c.total) || 0), 0);
        const projection: ProjectionSummary = {
          amount: projectionAmount,
          count: projectionCharges.length
        };

        const upcomingAsWithInvoice: ChargeWithInvoice[] = upcomingCharges
          .slice(0, 15)
          .map((c) => ({ ...c }))
          .sort((a, b) => {
            const da = this.parseDate(a.dueDate!);
            const db = this.parseDate(b.dueDate!);
            if (!da || !db) return 0;
            return da.getTime() - db.getTime();
          });

        const overdueAsWithInvoice: ChargeWithInvoice[] = overdueCharges
          .slice(0, 15)
          .map((c) => ({ ...c }))
          .sort((a, b) => {
            const da = this.parseDate(a.dueDate!);
            const db = this.parseDate(b.dueDate!);
            if (!da || !db) return 0;
            return da.getTime() - db.getTime();
          });

        const overdueTotal = overdueCharges.reduce((s, c) => s + (Number(c.total) || 0), 0);

        return {
          collection,
          projection,
          upcomingCharges: upcomingAsWithInvoice,
          overdueCharges: overdueAsWithInvoice,
          recentPayments,
          salesByDay,
          heatMapData,
          rates: this.computeRates(
            totalIssued,
            totalPaid,
            totalPending,
            collection.amount,
            overdueCharges.length,
            overdueTotal
          ),
          invoicesByStatus,
          loading: false
        };
      }),
      catchError((err) => of(this.emptyDashboard(err?.message || 'Error al cargar el dashboard')))
    );
  }

  private parseDate(d: string | undefined): Date | null {
    if (!d) return null;
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? null : dt;
  }

  /** La API no expone `amountPaid`; se aproxima con total − pendiente (`amountDue`). */
  private amountPaidFromInvoice(inv: InvoiceDTO): number {
    const total = Number(inv.total) || 0;
    const due = inv.amountDue != null ? Number(inv.amountDue) : NaN;
    if (!Number.isFinite(due)) return 0;
    return Math.max(0, total - due);
  }

  private computeCollection(
    invoices: InvoiceDTO[],
    periodFrom: Date,
    periodTo: Date
  ): CollectionSummary {
    let amount = 0;
    let count = 0;
    invoices.forEach((inv) => {
      const paidAt = this.parseDate(inv.paidAt);
      const amt = this.amountPaidFromInvoice(inv);
      if (!paidAt || amt <= 0) return;
      if (paidAt >= periodFrom && paidAt <= periodTo) {
        amount += amt;
        count += 1;
      }
    });
    return { amount, count };
  }

  private computeSalesByDay(
    invoices: InvoiceDTO[],
    periodFrom: Date,
    periodTo: Date
  ): { date: string; total: number; count: number }[] {
    const map = new Map<string, { total: number; count: number }>();
    invoices.forEach((inv) => {
      const d = this.parseDate(inv.issueDate) ?? this.parseDate(inv.createdAt);
      if (!d || d < periodFrom || d > periodTo) return;
      const key = d.toISOString().slice(0, 10);
      const total = Number(inv.total) || 0;
      const prev = map.get(key) ?? { total: 0, count: 0 };
      map.set(key, { total: prev.total + total, count: prev.count + 1 });
    });
    return Array.from(map.entries())
      .map(([date, v]) => ({ date, ...v }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }

  private computeHeatMap(
    invoices: InvoiceDTO[],
    periodFrom: Date,
    periodTo: Date
  ): { date: string; amount: number; count: number }[] {
    const map = new Map<string, { amount: number; count: number }>();

    invoices.forEach((inv) => {
      const paidAt = this.parseDate(inv.paidAt);
      const amount = this.amountPaidFromInvoice(inv);
      if (!paidAt || amount <= 0) return;
      if (paidAt < periodFrom || paidAt > periodTo) return;

      const key = paidAt.toISOString().slice(0, 10);
      const prev = map.get(key) ?? { amount: 0, count: 0 };
      map.set(key, {
        amount: prev.amount + amount,
        count: prev.count + 1
      });
    });

    const list = Array.from(map.entries()).map(([date, v]) => ({
      date,
      amount: v.amount,
      count: v.count
    }));
    list.sort((a, b) => a.date.localeCompare(b.date));
    return list;
  }

  private computeInvoicesByStatus(invoices: InvoiceDTO[]): Record<string, number> {
    const r: Record<string, number> = {};
    invoices.forEach((inv) => {
      const s = inv.status ?? 'UNKNOWN';
      r[s] = (r[s] ?? 0) + 1;
    });
    return r;
  }

  private computeRates(
    totalIssued: number,
    totalPaid: number,
    totalPending: number,
    periodCollected: number,
    _overdueCount: number,
    overdueAmount: number
  ): DashboardData['rates'] {
    const totalExpected = totalPaid + totalPending;
    const collectionRate =
      totalExpected > 0 ? Math.round((periodCollected / totalExpected) * 100) : 0;
    const overdueRate =
      totalExpected > 0
        ? Math.round((overdueAmount / totalExpected) * 100)
        : 0;

    return {
      collectionRate,
      overdueRate,
      totalIssued,
      totalPaid,
      totalPending
    };
  }

  private emptyDashboard(error?: string): DashboardData {
    return {
      collection: { amount: 0, count: 0 },
      projection: { amount: 0, count: 0 },
      upcomingCharges: [],
      overdueCharges: [],
      recentPayments: [],
      salesByDay: [],
      heatMapData: [],
      rates: {
        collectionRate: 0,
        overdueRate: 0,
        totalIssued: 0,
        totalPaid: 0,
        totalPending: 0
      },
      invoicesByStatus: {},
      loading: false,
      error
    };
  }
}
